/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// eslint-disable-next-line jira/filename-case
import React from 'react';
import Link from '@atlaskit/link/link';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { css, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import type { Boards, Users, Groups } from '../../model/index.tsx';

const noSubscriptionsAndBoards = defineMessage({
	id: 'directories.filters-v2.delete-modal.content.no-subscriptions-and-boards',
	defaultMessage: 'Are you sure you want to delete this filter?',
	description:
		'Generic informational message that is displayed in the delete modal when the filter can be deleted.',
});

const associatedItems = defineMessage({
	id: 'directories.filters-v2.delete-modal.content.associated-items',
	defaultMessage: "This filter is used elsewhere. Deleting it may impact other people's work.",
	description:
		'Generic informational message that is displayed in the delete modal when the filter can be deleted.',
});

const multipleSubscriptions = defineMessage({
	id: 'directories.filters-v2.delete-modal.content.multiple-subscriptions',
	defaultMessage: 'This filter has currently {number} subscriptions:',
	description:
		'Generic informational message that is displayed in the delete modal when the filter can be deleted.',
});

const singleSubscription = defineMessage({
	id: 'directories.filters-v2.delete-modal.content.single-subscription',
	defaultMessage: 'This filter has a subscription for ',
	description:
		'Generic informational message that is displayed in the delete modal when the filter can be deleted.',
});

const singleSubscriptionWarning = defineMessage({
	id: 'directories.filters-v2.delete-modal.content.single-subscription-warning',
	defaultMessage:
		'Deleting this filter will cause subscription emails for {subscriptionName} to no longer be delivered.',
	description:
		'Generic informational message that is displayed in the delete modal when the filter can be deleted. {name} is the name of the user or group.',
});

const multipleSubscriptionsWarning = defineMessage({
	id: 'directories.filters-v2.delete-modal.content.multiple-subscriptions-warning',
	defaultMessage:
		'Deleting this filter will cause subscription emails for these users and groups to no longer be delivered.',
	description:
		'Generic informational message that is displayed in the delete modal when the filter can be deleted.',
});

const multipleBoards = defineMessage({
	id: 'directories.filters-v2.delete-modal.content.multiple-boards',
	defaultMessage: 'This filter is currently being used by {number} boards:',
	description:
		'Generic informational message that is displayed in the delete modal when the filter can be deleted.',
});

const singleBoard = defineMessage({
	id: 'directories.filters-v2.delete-modal.content.single-board',
	defaultMessage: 'This filter is currently being used by ',
	description:
		'Generic informational message that is displayed in the delete modal when the filter can be deleted.',
});

const multipleBoardsWarning = defineMessage({
	id: 'directories.filters-v2.delete-modal.content.multiple-boards-warning',
	defaultMessage:
		"You'll need to remove this filters associations to the boards before it can be removed.",
	description:
		'Generic informational message that is displayed in the delete modal when the filter can be deleted.',
});

const formattedName = defineMessage({
	id: 'directories.filters-v2.delete-modal.content.formatted-name',
	defaultMessage: '{name}.',
	description: 'Formatted name of the user or group, in format "<name>."',
});

type Props = {
	boards: Boards;
	users: Users;
	groups: Groups;
};
const ContentWithoutBoardsAndSubscriptions = () => {
	const { formatMessage } = useIntl();
	return <Text>{formatMessage(noSubscriptionsAndBoards)}</Text>;
};

const ContentWithAssociatedSubscriptions = ({
	groups,
	users,
}: {
	groups: Groups;
	users: Users;
}) => {
	const { formatMessage } = useIntl();

	const totalSubscriptions = users.length + groups.length;
	const hasSingleUser = users.length === 1 && groups.length === 0;
	const hasSingleGroup = users.length === 0 && groups.length === 1;

	if (hasSingleUser) {
		const [{ displayName, href }] = users;
		return (
			<Stack space="space.075">
				<Text>
					{formatMessage(singleSubscription)}
					<Link href={href}>{formatMessage(formattedName, { name: displayName })}</Link>
				</Text>
				<div css={singleItemStyles}>
					<Text>
						{formatMessage(singleSubscriptionWarning, {
							subscriptionName: displayName,
						})}
					</Text>
				</div>
			</Stack>
		);
	}
	if (hasSingleGroup) {
		const [{ name }] = groups;
		return (
			<Stack space="space.075">
				<Text>
					{formatMessage(singleSubscription)}
					{formatMessage(formattedName, { name })}
				</Text>
				<div css={singleItemStyles}>
					<Text>
						{formatMessage(singleSubscriptionWarning, {
							subscriptionName: name,
						})}
					</Text>
				</div>
			</Stack>
		);
	}

	return (
		<Stack>
			<Text>
				{formatMessage(multipleSubscriptions, {
					number: totalSubscriptions,
				})}
			</Text>
			<Stack as="ul">
				{users.map(({ displayName, href }, index) => (
					<li key={index}>
						<Link href={href}>{displayName}</Link>
					</li>
				))}
				{groups.map(({ name }, index) => (
					<li key={index}>
						<Text>{name}</Text>
					</li>
				))}
			</Stack>
			<div css={textStyles}>
				<Text>{formatMessage(multipleSubscriptionsWarning)}</Text>
			</div>
		</Stack>
	);
};

const ContentWithAssociatedBoards = ({ boards }: { boards: Boards }) => {
	const { formatMessage } = useIntl();
	if (boards.length === 1) {
		return (
			<Stack space="space.150">
				<Text>
					{formatMessage(singleBoard)}
					<Link href={boards[0].href}>
						{formatMessage(formattedName, { name: boards[0].name })}
					</Link>
				</Text>
				<Text>{formatMessage(multipleBoardsWarning)}</Text>
			</Stack>
		);
	}
	return (
		<Stack>
			<Text>
				{formatMessage(multipleBoards, {
					number: boards.length,
				})}
			</Text>
			<Stack as="ul">
				{boards.map(({ name, href }, index) => (
					<li key={index}>
						<Link href={href}>{name}</Link>
					</li>
				))}
			</Stack>
			<div css={textStyles}>
				<Text>{formatMessage(multipleBoardsWarning)}</Text>
			</div>
		</Stack>
	);
};

export const Content = ({ boards, groups, users }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	if (users.length === 0 && groups.length === 0 && boards.length === 0) {
		return <ContentWithoutBoardsAndSubscriptions />;
	}

	return (
		<Stack space="space.150">
			<Text>{formatMessage(associatedItems)}</Text>
			{boards.length > 0 && <ContentWithAssociatedBoards boards={boards} />}
			{(users.length > 0 || groups.length > 0) && (
				<ContentWithAssociatedSubscriptions users={users} groups={groups} />
			)}
		</Stack>
	);
};

const singleItemStyles = css({
	marginTop: token('space.075'),
});

const textStyles = css({
	marginTop: token('space.150'),
});

export default Content;
